import React from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import cardBackground from 'assets/card-background.svg';
import { generateTestId } from '../../../app/functions';
import { getMediaPath } from '../../../app/functions';
import { P } from 'pino';

const SectionWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  overflow: 'hidden',
  padding: '0',
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: '0 auto',
  padding: '0px 24px 0px 24px',
  display: 'flex',
  gap: '12px',
  flexWrap: 'nowrap',
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  WebkitOverflowScrolling: 'touch',
  [theme.breakpoints.up('sm')]: {
    padding: '0px 28px 0px 28px',
    gap: '14px',
  },
  [theme.breakpoints.up('md')]: {
    padding: '0px 32px 0px 32px',
    gap: '24px',
    justifyContent: 'center',
    overflowX: 'auto',
  },
  [theme.breakpoints.up('lg')]: {
    padding: '0px 36px 0px 36px',
    overflowX: 'auto',
    justifyContent: 'center',
    maxWidth: '100%',
    padding: '0 20px',
    gap: '40px',
    '& > *': {
      flexShrink: 0,
    },
  },
  [theme.breakpoints.up('xl')]: {
    padding: '0px 36px 0px 36px',
    width: '1600px',
    height: '200px',
    gap: '60px',
    justifyContent: 'center',
    overflowX: 'hidden',
  },
}));

const BenefitCard = styled(Box)(({ theme }) => ({
  flex: '0 0 auto',
  width: '130px',
  height: 'auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '6px',
  backgroundImage: `url(${cardBackground})`,
  backgroundSize: '100% 100%',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  borderRadius: '12px',
  padding: '8px 4px',
  transition: 'transform 0.3s ease',

  [theme.breakpoints.up('sm')]: {
    width: '170px',
    height: 'auto',
    padding: '10px 6px',
    gap: '6px',
  },
  [theme.breakpoints.up('md')]: {
    width: '160px',
    height: 'auto',
    padding: '16px 8px',
    gap: '12px',
  },
  [theme.breakpoints.up('lg')]: {
    width: '200px',
    height: 'auto',
    padding: '0px 10px',
    marginTop: '0px',
    marginBottom: '0px',
    gap: '12px',
  },
  [theme.breakpoints.up('xl')]: {
    width: '220px',
    height: 'auto',
    padding: '0px 8px',
    gap: '12px',
  },
}));

const BenefitIcon = styled('img')(({ theme }) => ({
  width: '40px',
  height: '40px',
  objectFit: 'contain',
  margin: '9px 3.6px 8px',
  [theme.breakpoints.up('sm')]: {
    width: '50px',
    height: '50px',
    margin: '6px 3px',
  },
  [theme.breakpoints.up('md')]: {
    margin: '15px 5px 10px',
  },
  [theme.breakpoints.up('lg')]: {
    width: '63px',
    height: '63px',
    margin: '18px 10px 12px',
  },
  [theme.breakpoints.up('xl')]: {
    width: '60px',
    height: '60px',
    flexShrink: 0,
    margin: '0 10px',
  },
}));

const BenefitText = styled(Box)(({ theme, customStyles }) => ({
  color: theme.palette.benefitsSection.title,
  fontSize: '12px',
  lineHeight: '100%',
  fontStyle: 'normal',
  fontWeight: 'bold',
  textAlign: 'center',
  maxWidth: '100%',
  wordWrap: 'break-word',
  paddingBottom: '10px',
  ...customStyles,
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px',
    lineHeight: '100%',
    height: 'auto',
    fontStyle: 'normal',
    fontWeight: 700,
    textAlign: 'center',
    maxWidth: '100%',
    paddingBottom: '12px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    width: '100%',
    height: 'auto',
    paddingBottom: '14px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '16px',
    width: '100%',
    height: 'auto',
    padding: '0px 5px 16px 5px',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '20px',
    width: '100%',
    height: 'auto',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'normal',
    textTransform: 'capitalize',
    textAlign: 'center',
    paddingBottom: '0px',
  },
}));

const TopInfoCardComponent = ({data, testID}) => {
  if(!data?.benefits?.length && !data.topInfoCards?.length) return null;
  const topInfoCards = data.topInfoCards || data.benefits;
  return (
    <SectionWrapper testId={generateTestId(testID, 'section-wrapper')}>
      <ContentWrapper testId={generateTestId(testID, 'content-wrapper')}>
        {topInfoCards.map((benefit,index) => (
          <BenefitCard key={index} testId={generateTestId(testID, `benefit-card-${index}`)}>
            <BenefitIcon src={getMediaPath(benefit.icon)} alt={benefit.text} testid={generateTestId(testID, `benefit-icon-${index}`)} />
            <BenefitText
              testId={generateTestId(testID, `benefit-text-${index}`)}
            >
              {benefit.text}
            </BenefitText>
          </BenefitCard>
        ))}
      </ContentWrapper>
    </SectionWrapper>
  );
};

export default TopInfoCardComponent;
